import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Plane, Map, AlertTriangle, Zap, RefreshCw, Shuffle, CheckCircle, Clock, Activity } from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import "../../marketing.css";

const MODULES = [
  { icon: Activity, name: "Flight Ops Board", desc: "Live flight status board showing all rotations, delay reasons, crew assignments and ground handler readiness for every active flight.", badge: "OCC LIVE", metric: "Live", color: "text-sky-400 bg-sky-500/10 border-sky-500/20" },
  { icon: Map, name: "OPS Map", desc: "Geographic operations map with live aircraft positions, station status indicators and crew location overlays across all bases.", badge: "GEO-LIVE", metric: "Live", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  { icon: AlertTriangle, name: "IRROPS Console", desc: "Irregular operations workspace — priority-sorted disruption queue, crew impact scoring and cascade-effect recovery tools.", badge: "IRROPS", metric: "<5min", color: "text-red-400 bg-red-500/10 border-red-500/20" },
  { icon: Zap, name: "AI Delay Prediction", desc: "Machine-learning departure confidence scoring. Predicts delays 90 minutes before STD using ground task progress, crew readiness and weather inputs.", badge: "AI ENGINE", metric: "90min", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
  { icon: RefreshCw, name: "Disruption Management", desc: "End-to-end disruption recovery: re-routing options, slot recovery, crew re-assignment with FDTL validation and passenger impact reporting.", badge: "RECOVERY", metric: "Auto", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  { icon: Shuffle, name: "Crew Deployment", desc: "Cross-base crew positioning matrix with type rating currency, recency tracking and optimal positioning recommendations.", badge: "MULTI-BASE", metric: "All", color: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
];

const LIVE_FLIGHTS = [
  { flight: "FS 201", route: "BOM → DEL", std: "06:30", status: "ON TIME", confidence: 94, statusColor: "text-green-400 bg-green-500/10" },
  { flight: "FS 334", route: "DEL → HYD", std: "07:15", status: "DELAYED 25m", confidence: 61, statusColor: "text-red-400 bg-red-500/10" },
  { flight: "FS 782", route: "BLR → DEL", std: "08:00", status: "ON TIME", confidence: 88, statusColor: "text-green-400 bg-green-500/10" },
  { flight: "FS 119", route: "CCU → BOM", std: "08:45", status: "WATCH", confidence: 72, statusColor: "text-amber-400 bg-amber-500/10" },
];

export default function FlightOpsPage() {
  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ── */}
      <section className="pt-28 pb-20 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 50% at 30% 40%, rgba(14,165,233,0.08) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 px-3 py-1.5 rounded mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 live-dot" />
                <span className="text-[10px] text-sky-400 marketing-font-mono uppercase font-bold tracking-widest">Flight Operations</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-black marketing-font-heading leading-tight mb-5">
                OCC-grade flight ops<br />
                <span className="text-sky-400">without the OCC price tag.</span>
              </h1>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                Live flight board, AI delay prediction, IRROPS recovery and crew deployment — all connected to the same fatigue data your crew ops team uses.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/marketing/demo" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5" data-testid="flightops-demo-btn">
                  Request Demo <ArrowRight size={16} />
                </Link>
                <Link to="/login" className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 px-7 py-3.5 transition-all">
                  See Live Board <ChevronRight size={16} />
                </Link>
              </div>
            </div>

            {/* Live flight board mockup */}
            <div className="bg-[#0c1929]/60 border border-sky-500/15 p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 live-dot" />
                <span className="text-[10px] text-sky-400 marketing-font-mono uppercase tracking-widest font-bold">Flight Ops Board — Live</span>
                <span className="ml-auto text-[9px] text-slate-600 marketing-font-mono">06:18 IST</span>
              </div>
              <div className="space-y-2">
                {LIVE_FLIGHTS.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 bg-[#07111f] border border-white/5 p-3">
                    <div className="w-14 shrink-0">
                      <div className="text-xs font-bold text-white marketing-font-mono">{f.flight}</div>
                      <div className="text-[9px] text-slate-600">{f.route}</div>
                    </div>
                    <div className="text-[10px] text-slate-500 marketing-font-mono w-12">{f.std}</div>
                    <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-sky-500/50 rounded-full" style={{ width: `${f.confidence}%` }} />
                    </div>
                    <span className={`text-[9px] font-bold marketing-font-mono px-2 py-1 ${f.statusColor}`}>{f.status}</span>
                    <span className="text-[9px] text-sky-400 marketing-font-mono w-8 text-right">{f.confidence}%</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <div className="text-[9px] text-slate-700 marketing-font-mono">Confidence = AI departure probability score</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6 MODULES ── */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <div className="text-[10px] text-sky-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">Flight ops suite</div>
            <h2 className="text-3xl font-black marketing-font-heading">Six modules. Every flight, controlled.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              const [textColor, bgColor, borderColor] = m.color.split(" ");
              return (
                <div key={i} className={`bg-[#0c1929]/50 border ${borderColor} hover:border-opacity-60 p-6 card-glow transition-all`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-9 h-9 ${bgColor} border ${borderColor} flex items-center justify-center`}>
                      <Icon size={15} className={textColor} />
                    </div>
                    <div>
                      <div className={`text-[9px] marketing-font-mono font-bold uppercase tracking-widest ${textColor}`}>{m.badge}</div>
                      <div className="text-sm font-bold text-white marketing-font-heading">{m.name}</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── AI DELAY PREDICTION ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f] grid-texture">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-4">AI Delay Prediction</div>
              <h2 className="text-3xl font-black marketing-font-heading mb-5">Know a delay is coming<br /><span className="text-orange-400">90 minutes before STD.</span></h2>
              <p className="text-slate-400 leading-relaxed mb-8">The AI model scores departure confidence by ingesting ground task progress, crew fatigue status, aircraft readiness and historical delay patterns. Not a guess — a scored prediction.</p>
              <div className="space-y-3">
                {[
                  "Ground task completion rate vs. historical benchmark",
                  "Crew fatigue scores and FDTL remaining headroom",
                  "Weather and ATC ATIS inputs",
                  "Aircraft technical status from Engineering module",
                  "Historical delay patterns for same rotation + time",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={13} className="text-orange-400 shrink-0" /> {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {[
                { label: "Confidence ≥ 80%", desc: "Departure on track. No action required.", badge: "ON TRACK", color: "border-green-500/20 bg-green-500/5 text-green-400" },
                { label: "Confidence 60–79%", desc: "Watch category. Pre-position recovery crew and slot.", badge: "WATCH", color: "border-amber-500/20 bg-amber-500/5 text-amber-400" },
                { label: "Confidence < 60%", desc: "High delay risk. IRROPS console activated automatically.", badge: "IRROPS RISK", color: "border-red-500/20 bg-red-500/5 text-red-400" },
              ].map((level, i) => (
                <div key={i} className={`border ${level.color.split(" ").find(c => c.startsWith("border"))} ${level.color.split(" ").find(c => c.startsWith("bg"))} p-5`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-[9px] marketing-font-mono font-black uppercase border border-current/30 px-2 py-1 ${level.color.split(" ").find(c => c.startsWith("text"))}`}>{level.badge}</span>
                    <span className="font-bold text-white text-sm">{level.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{level.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl font-black marketing-font-heading mb-4">Stop reacting to delays.<br /><span className="text-orange-400">Start predicting them.</span></h2>
          <p className="text-slate-400 max-w-md mx-auto mb-8">Live demo with your route structure and delay history. See the AI score your flights.</p>
          <Link to="/marketing/demo" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-9 py-4 transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/20">
            Request Demo <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
